import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import FeatureAccessEditor from '../components/users/FeatureAccessEditor';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, Edit, AlertCircle, Key, Check, Mail, Ban, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { GmailSettings } from '../components/email';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// `embedded` renders the page bare for use inside Settings -> Users, where the
// surrounding Layout and page title already exist.
const Users = ({ embedded = false }) => {
  const { getAuthHeaders, user: authUser } = useAuth();
  const canGrantSuper = !!authUser?.roles?.includes('super_admin'); // 10/07/2026
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    roles: ['broker'],
    contact_id: '',
    collects_fees: false,
    collects_extras: false,
    uw_toolkit_enabled: false,
    slack_user_id: '',
    features: {},
    data_scope: '',
  });

  const applyRolesFromContact = (contactId, currentRoles) => {
    if (!contactId) return currentRoles || [];
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact) return currentRoles || [];

    const types = contact.contact_types || [];
    const suggested = new Set(currentRoles || []);

    if (types.includes('Broker')) suggested.add('broker');
    if (types.includes('ISO')) suggested.add('iso');
    if (types.includes('Funder')) suggested.add('funder');
    if (types.includes('Merchant')) suggested.add('merchant');
    if (types.includes('Investor')) {
      suggested.add('syndicator');
      suggested.add('investor');
    }

    return Array.from(suggested);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const [usersRes, contactsRes] = await Promise.all([
        axios.get(`${API}/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/contacts`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        phone: user.phone || '',
        roles: user.roles || ['broker'],
        contact_id: user.contact_id || '',
        collects_fees: user.collects_fees ?? false,
        collects_extras: user.collects_extras ?? false,
        uw_toolkit_enabled: user.uw_toolkit_enabled ?? false,
        slack_user_id: user.slack_user_id || '',
        features: user.features || {},
        data_scope: user.data_scope || '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        roles: ['broker'],
        contact_id: '',
        collects_fees: false,
        collects_extras: false,
        uw_toolkit_enabled: false,
        slack_user_id: '',
        features: {},
        data_scope: '',
      });
    }
    setError('');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // 10/08 (Josh): a user must be attached to a contact — the API refuses otherwise.
    if (!formData.contact_id) {
      setError('Pick the contact this user belongs to (create the contact first if it does not exist).');
      return;
    }

    try {
      if (editingUser) {
        await axios.put(
          `${API}/users/${editingUser.id}`,
          { name: formData.name, email: formData.email, phone: formData.phone, roles: formData.roles, contact_id: formData.contact_id, collects_fees: !!formData.collects_fees, collects_extras: !!formData.collects_extras, uw_toolkit_enabled: !!formData.uw_toolkit_enabled, slack_user_id: (formData.slack_user_id || '').trim(), features: formData.features || {}, data_scope: formData.data_scope || null },
          { headers: getAuthHeaders() }
        );
        toast.success('User updated successfully');
      } else {
        // Create user
        const response = await axios.post(`${API}/users`, formData, { headers: getAuthHeaders() });
        toast.success('User created successfully');
        
        // Send welcome email if checked
        if (sendWelcomeEmail && response.data?.id) {
          try {
            await axios.post(`${API}/settings/send-welcome-email`, {
              user_id: response.data.id,
              user_email: formData.email,
              user_name: formData.name,
              temporary_password: formData.password
            }, { headers: getAuthHeaders() });
            toast.success('Welcome email sent');
          } catch (emailErr) {
            toast.error('User created but failed to send welcome email');
          }
        }
      }
      setDialogOpen(false);
      setSendWelcomeEmail(true);
      fetchUsers();
    } catch (err) {
      const errorMsg = err.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        setError(errorMsg.map(e => `${e.loc[1]}: ${e.msg}`).join(', '));
      } else {
        setError(errorMsg || 'Operation failed');
      }
    }
  };

  const handleToggleSuspend = async (user) => {
    const suspending = !user.suspended;
    if (suspending && !window.confirm(
      `Suspend ${user.name}? They will be signed out immediately and cannot log in ` +
      `or use MILA in Slack until you restore them. Their record and history are kept.`
    )) return;
    try {
      await axios.put(
        `${API}/users/${user.id}/${suspending ? 'suspend' : 'unsuspend'}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(suspending ? `${user.name} suspended` : `${user.name} restored`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update that user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeaders() });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-800',
      admin: 'bg-red-100 text-red-800',
      broker: 'bg-blue-100 text-blue-800',
      syndicator: 'bg-green-100 text-green-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/users/${changingPasswordUser.id}/change-password`,
        null,
        { 
          headers: getAuthHeaders(),
          params: { new_password: newPassword }
        }
      );
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setChangingPasswordUser(null);
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  const handleResendWelcomeEmail = async (user) => {
    if (!window.confirm(`Send welcome email to ${user.name} (${user.email})?`)) return;
    try {
      await axios.post(`${API}/settings/send-welcome-email`, {
        user_id: user.id,
        user_email: user.email,
        user_name: user.name,
      }, { headers: getAuthHeaders() });
      toast.success(`Welcome email sent to ${user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send welcome email');
    }
  };


  const content = (
      <div className="px-4 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          {embedded
            ? <div />
            : <h1 className="text-3xl font-bold text-gray-900" data-testid="users-title">Users</h1>}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} data-testid="create-user-button">
                <Plus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="user-dialog" className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Edit User' : 'Create User'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Full name (min 2 characters)"
                    required
                    data-testid="user-form-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                    data-testid="user-form-email-input"
                  />
                </div>

                {!editingUser && (
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Min 8 chars, 1 upper, 1 number, 1 special"
                      required
                      data-testid="user-form-password-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      8+ chars, 1 uppercase, 1 number, 1 special character
                    </p>
                  </div>
                )}

                {/* Send Welcome Email Option - Only for new users */}
                {!editingUser && (
                  <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="sendWelcomeEmail"
                      checked={sendWelcomeEmail}
                      onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    <label htmlFor="sendWelcomeEmail" className="flex-1 cursor-pointer">
                      <span className="font-medium text-sm text-blue-900">Send Welcome Email</span>
                      <p className="text-xs text-blue-700">
                        Send login credentials and welcome message to the user's email address
                      </p>
                    </label>
                  </div>
                )}

                <div>
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Phone number"
                    data-testid="user-form-phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="slack_user_id">Slack Member ID (optional)</Label>
                  <Input
                    id="slack_user_id"
                    value={formData.slack_user_id}
                    onChange={(e) => setFormData({ ...formData, slack_user_id: e.target.value })}
                    placeholder="U0AUMMZD0E6"
                    data-testid="user-form-slack-id-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Links this person's Slack account to this CRM user. MILA in
                    Slack then answers <strong>as them</strong> — scoped to the deals
                    they're involved in — instead of as an anonymous admin. Find it in
                    Slack: their profile → ••• → "Copy member ID". Leave blank to unlink.
                  </p>
                </div>

                <div>
                  <Label>Feature Access (what this user can see)</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Pick the features this login gets. Read only shows the pages; read &amp; write adds the actions. A preset fills everything in one click.
                  </p>
                  {canGrantSuper && (
                    <label className="flex items-start space-x-2 mb-3">
                      <input type="checkbox" checked={formData.roles.includes('super_admin')} className="mt-1 rounded border-gray-300"
                             onChange={(e) => setFormData({ ...formData, roles: e.target.checked ? Array.from(new Set([...formData.roles, 'super_admin', 'admin'])) : formData.roles.filter((r) => r !== 'super_admin') })} />
                      <span className="text-sm">
                        <span className="font-medium block">Super admin</span>
                        <span className="text-xs text-gray-500">Everything, plus user management, API keys, deletes and the danger zone. Ignores the boxes below.</span>
                      </span>
                    </label>
                  )}
                  <FeatureAccessEditor
                    features={formData.features}
                    scope={formData.data_scope}
                    onChange={({ features, scope, roles }) => setFormData({
                      ...formData, features, data_scope: scope || '',
                      // a preset also sets the legacy role it stands for (role-driven screens keep working); a super admin keeps that role
                      ...(roles ? { roles: Array.from(new Set([...(formData.roles.includes('super_admin') ? ['super_admin', 'admin'] : []), ...roles])) } : {}),
                    })}
                  />
                </div>

                <div>
                  <Label htmlFor="contact_id">Link to Contact *</Label>
                  <Select 
                    value={formData.contact_id || ""} 
                    onValueChange={(value) => {
                      const newRoles = applyRolesFromContact(value, formData.roles);
                      setFormData({ ...formData, contact_id: value, roles: newRoles });
                    }}
                  >
                    <SelectTrigger data-testid="user-form-contact-select">
                      <SelectValue placeholder="Pick the contact this login belongs to" />
                    </SelectTrigger>
                    <SelectContent>
                      {contacts.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.company_name}
                          {contact.contact_types && contact.contact_types.length > 0 && 
                            ` (${contact.contact_types.join(', ')})`
                          }
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Link this user account to a contact for role-based data access
                  </p>
                </div>

                {/* Origination entitlement (was "Collects fees", renamed 11/04 per Josh) */}
                <div>
                  <div className="mt-4 flex items-start space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.collects_fees}
                      onChange={(e) => setFormData({ ...formData, collects_fees: e.target.checked })}
                      className="mt-1 rounded border-gray-300"
                    />
                    <span className="text-sm">
                      <span className="font-medium block">Origination entitlement</span>
                      <span className="text-xs text-gray-500">
                        If checked, this user will receive their share of admin fees (including bounce fees) on syndicated deals.
                      </span>
                    </span>
                  </div>
                </div>

                {/* Collects extras? for amounts above payback target */}
                <div>
                  <div className="mt-4 flex items-start space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.collects_extras}
                      onChange={(e) => setFormData({ ...formData, collects_extras: e.target.checked })}
                      className="mt-1 rounded border-gray-300"
                    />
                    <span className="text-sm">
                      <span className="font-medium block">Collects Extras</span>
                      <span className="text-xs text-gray-500">
                        If checked, this syndicator receives their share of "extras" (collections above payback target). 
                        Otherwise, extras go to Power Funding Solutions.
                      </span>
                    </span>
                  </div>
                </div>

                {error && (
                  <Alert variant="destructive" data-testid="user-form-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="user-form-submit-button">
                    {editingUser ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No users found. Create your first user.
              </div>
            ) : (
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Linked Contact</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-center w-24">Entitlement</TableHead>
                    <TableHead className="text-center w-16">UW</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const linkedContact = contacts.find(c => c.id === user.contact_id);
                    return (
                      <TableRow
                        key={user.id}
                        data-testid={`user-row-${user.id}`}
                        className={user.suspended ? 'opacity-60 bg-gray-50' : undefined}
                      >
                        <TableCell className="font-medium">
                          {user.name}
                          {user.suspended && (
                            <Badge
                              className="ml-2 bg-amber-100 text-amber-800"
                              data-testid={`suspended-badge-${user.id}`}
                            >
                              Suspended
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {user.roles?.map((role) => (
                              <Badge key={role} className={getRoleBadgeColor(role)}>{role}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {linkedContact ? (
                            <span className="text-sm text-blue-600">{linkedContact.company_name}</span>
                          ) : (
                            <span className="text-sm text-gray-400">Not linked</span>
                          )}
                        </TableCell>
                        <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-center">
                          {user.collects_fees && (
                            <Check className="h-4 w-4 text-green-600 mx-auto" />
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {user.uw_toolkit_enabled && (
                            <Check className="h-4 w-4 text-blue-600 mx-auto" title="UW Toolkit Access" />
                          )}
                        </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResendWelcomeEmail(user)}
                            title="Resend Welcome Email"
                            data-testid={`resend-email-${user.id}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenDialog(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setChangingPasswordUser(user);
                              setPasswordDialogOpen(true);
                            }}
                            title="Change Password"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleSuspend(user)}
                            title={user.suspended ? 'Restore access' : 'Suspend user'}
                            data-testid={`suspend-user-${user.id}`}
                          >
                            {user.suspended
                              ? <RotateCcw className="h-4 w-4 text-green-600" />
                              : <Ban className="h-4 w-4 text-amber-600" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(user.id)}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Password Change Dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Password for {changingPasswordUser?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => {
                  setPasswordDialogOpen(false);
                  setNewPassword('');
                  setChangingPasswordUser(null);
                }}>
                  Cancel
                </Button>
                <Button type="submit">Change Password</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Gmail Settings Section */}
        <div className="mt-8">
          <GmailSettings getAuthHeaders={getAuthHeaders} />
        </div>
      </div>
  );

  return embedded ? content : <Layout>{content}</Layout>;
};

export default Users;
